'use client';
import React, { startTransition, useEffect } from 'react';
import subMonths from 'date-fns/subMonths';

import Layout from '../lib/components/Layout';
import CalendarService from '../lib/services/CalendarService';
import ColumnsDefinition from './ColumnsDef';
// import { formatRequestDate } from '../lib/utils';
import { formatRequestDate, injectLogos, injectQuotes, sortAnalystRatings } from '../lib/utils';

import { fetchAnalystRatings, fetchRatingsAnalysts } from './Services';
import { ICalendarProps, IDateRange } from '../lib/interfaces';
import { Ratings } from '@benzinga/calendar-manager';
import styled from '@benzinga/themetron';

export const AnalystRatingsColumnsDef = ColumnsDefinition;
export const fetchAnalystRatingsData = fetchAnalystRatings;
export { fetchRatingsAnalysts };

const getTabDataByTabIndex = (index: number, dataTabs: [Ratings[], Ratings[], Ratings[], Ratings[]]) => {
  return dataTabs[index];
};

export const AnalystRatingCalendar: React.FC<ICalendarProps> = ({
  additionalFetchParams,
  calendar,
  calendarData,
  hasStockTableStyling,
  height,
  hiddenColumns,
  hideFilters,
  initialData,
  initialDateRange,
  initialTab,
  initialTicker,
  isRouteLoading,
  leftSlot,
  onDataLoad,
  onReady,
  onTabChange: onPropsTabChange,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [allData, setAllData] = React.useState<Ratings[]>(initialData || []);
  const [upgradesData, setUpgradesData] = React.useState<Ratings[]>(() => {
    const ratings = sortAnalystRatings(allData);
    return ratings.upgrades || [];
  });
  const [downgradesData, setDowngradesData] = React.useState<Ratings[]>(() => {
    const ratings = sortAnalystRatings(allData);
    return ratings.downgrades || [];
  });
  const [initiationsData, setInitiationsData] = React.useState<Ratings[]>(() => {
    const ratings = sortAnalystRatings(allData);
    return ratings.initiations || [];
  });
  const [dataUpdating, setDataUpdating] = React.useState(false);
  const [activeTabIndex, setActiveTabIndex] = React.useState(getTabIndex(initialTab));
  const dataTabs: [Ratings[], Ratings[], Ratings[], Ratings[]] = React.useMemo(
    () => [allData, upgradesData, downgradesData, initiationsData],
    [allData, downgradesData, initiationsData, upgradesData],
  );
  const [rowData, setRowData] = React.useState(() => {
    const tabData = getTabDataByTabIndex(activeTabIndex, dataTabs);
    return tabData || [];
  });
  let dateRange: IDateRange = {};

  if (initialDateRange) {
    dateRange = initialDateRange;
  } else {
    dateRange = {
      date_from: formatRequestDate(subMonths(new Date(), 1)),
      date_to: formatRequestDate(new Date()),
    };
  }

  const setAnalystRatings = React.useCallback(
    (items: any[]) => {
      setDataUpdating(true);

      const ratings = sortAnalystRatings(items);

      setAllData(items ?? []);
      setUpgradesData(ratings.upgrades);
      setDowngradesData(ratings.downgrades);
      setInitiationsData(ratings.initiations);
      onDataLoad && onDataLoad(items ?? []);
      setDataUpdating(false);
    },
    [onDataLoad],
  );

  React.useEffect(() => {
    if (calendarData?.serverSide) {
      setAnalystRatings(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarData?.serverSide, initialData]);

  function onTableReady() {
    if (initialData) {
      setAnalystRatings(initialData);
    } else {
      const params = {
        'parameters[date_from]': dateRange.date_from,
        'parameters[date_to]': dateRange.date_to,
      };
      if (initialTicker) {
        params['parameters[tickers]'] = initialTicker;
      }
      fetchData(params);
    }

    if (onReady) {
      setTimeout(() => onReady());
    }
  }

  const fetchData = React.useCallback(
    (filterParams: any) => {
      const query = {
        pagesize: 1000,
        ...(additionalFetchParams && additionalFetchParams),
        ...filterParams,
      };

      setLoading(true);

      const Calendar = new CalendarService();

      Calendar.getAnalystRatings(query)
        .then(({ data }: any) => {
          const items: any = [];
          if (Array.isArray(data.ratings) && data.ratings.length) {
            return data.ratings;
            // This is for if we can get await to work via babel update
            // let transformedData = data.ratings;
            // // transformedData = await injectLogos(transformedData);
            // // transformedData = await injectQuotes(transformedData);
            // return transformedData;
          }
          return items;
        })
        .then((ratings: Ratings[]) => {
          const reqs = [injectLogos(ratings), injectQuotes(ratings)];
          Promise.all(reqs)
            .then(res => {
              setAnalystRatings(res[1]);
              setLoading(false);
            })
            .catch(() => {
              setAnalystRatings(ratings);
              setLoading(false);
            });
        });
    },
    [additionalFetchParams, setAnalystRatings],
  );

  function tabs() {
    return [
      {
        hasParentUrl: true,
        key: 'all',
        link: 'https://www.benzinga.com/analyst-stock-ratings',
        title: `All Ratings (${allData.length})`,
      },
      {
        key: 'upgrades',
        link: 'https://www.benzinga.com/analyst-stock-ratings',
        title: `Upgrades (${upgradesData.length})`,
      },
      {
        key: 'downgrades',
        link: 'https://www.benzinga.com/analyst-stock-ratings',
        title: `Downgrades (${downgradesData.length})`,
      },
      {
        key: 'initiations',
        link: 'https://www.benzinga.com/analyst-stock-ratings',
        title: `Initiations (${initiationsData.length})`,
      },
    ];
  }

  function onTabChange(index: number) {
    if (isRouteLoading) {
      return;
    }
    startTransition(() => {
      setActiveTabIndex(index);
      const data = getTabDataByTabIndex(index, dataTabs);
      if (data && data.length) {
        setTableData(data);
      }

      if (onPropsTabChange) {
        const targetTab = tabs()[index];
        if (targetTab) {
          onPropsTabChange(targetTab.hasParentUrl || targetTab.key);
        }
      }
    });
  }

  function setTableData(data: any) {
    setRowData(data);
  }

  function getTabIndex(index: any): number {
    if (typeof index === 'number') {
      return index;
    } else if (typeof index === 'string') {
      const targetTabIndex = tabs().findIndex(tab => tab.key === index);

      return targetTabIndex > 0 ? targetTabIndex : 0;
    }

    return 0;
  }

  useEffect(() => {
    if (!dataUpdating) {
      setTableData(getTabDataByTabIndex(activeTabIndex, dataTabs));
    }
  }, [activeTabIndex, dataUpdating, dataTabs]);

  return (
    <AnalystRatingCalendarWrapper>
      <Layout
        calendar={calendar}
        calendarData={calendarData}
        columnDefs={ColumnsDefinition}
        enableSearch
        hasStockTableStyling={hasStockTableStyling}
        height={height}
        hiddenColumns={hiddenColumns}
        hideFilters={hideFilters}
        initialDateRange={dateRange}
        initialTabIndex={getTabIndex(initialTab)}
        isGated={true}
        isRouteLoading={isRouteLoading}
        leftSlot={leftSlot}
        onFilterChanged={fetchData}
        onTabChange={onTabChange}
        onTableReady={onTableReady}
        tableData={rowData}
        tableLoading={loading ?? isRouteLoading ?? dataUpdating}
        tabs={tabs()}
        ticker={initialTicker}
      />
    </AnalystRatingCalendarWrapper>
  );
};

const AnalystRatingCalendarWrapper = styled.div`
  .buy-now-button {
    border-radius: 4px;
    background-color: #f2faf2;
    color: rgb(0, 153, 0) !important;
    padding: 2px 4px;
    white-space: nowrap;

    &:hover {
      background-color: #cef1ce;
    }
  }
`;
